import api from './client';
import type {
  AdWithTargeting,
  CreateAdInput,
  UpdateAdInput,
  UploadUrlResponse,
  UploadResponse,
} from './types';

export interface PaginatedAds {
  ads: AdWithTargeting[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface AdsFilters {
  campaignId?: string;
  adType?: string;
  mediaType?: string;
  status?: string;
  search?: string;
  country?: string;
  platform?: string;
  language?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  startDate?: string;
  endDate?: string;
  hideTest?: boolean;
}

/**
 * Get ads with pagination and filters
 */
export async function getAds(filters: AdsFilters = {}): Promise<PaginatedAds> {
  const { campaignId, adType, mediaType, status, search, country, platform, language, page = 1, pageSize = 20, sortBy, sortOrder, startDate, endDate, hideTest } = filters;
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (hideTest) params.set('hide_test', 'true');
  if (campaignId) params.set('campaign_id', campaignId);
  if (adType) params.set('ad_type', adType);
  if (mediaType) params.set('media_type', mediaType);
  if (status) params.set('status', status);
  if (search) params.set('search', search);
  if (country) params.set('country', country);
  if (platform) params.set('platform', platform);
  if (language) params.set('language', language);
  if (sortBy) params.set('sort_by', sortBy);
  if (sortOrder) params.set('sort_order', sortOrder);
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  return api.get<PaginatedAds>(`/admin/ads?${params.toString()}`);
}

/**
 * Create a new ad
 */
export async function createAd(data: CreateAdInput): Promise<AdWithTargeting> {
  return api.post<AdWithTargeting>('/admin/ads', data);
}

/**
 * Update an existing ad
 */
export async function updateAd(id: string, data: UpdateAdInput): Promise<AdWithTargeting> {
  return api.put<AdWithTargeting>(`/admin/ads/${id}`, data);
}

/**
 * Delete an ad
 */
export async function deleteAd(id: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/admin/ads/${id}`);
}

/**
 * Duplicate multiple ads to another campaign
 */
export async function duplicateAds(
  adIds: string[],
  targetCampaignId: string
): Promise<{ duplicated_count: number; new_ad_ids: string[] }> {
  return api.post<{ duplicated_count: number; new_ad_ids: string[] }>(
    '/admin/ads/duplicate',
    { ad_ids: adIds, target_campaign_id: targetCampaignId }
  );
}

/**
 * Archive ads (one-way). Archived ads are hidden from the default list,
 * never served, and kept only for statistics.
 */
export async function archiveAds(
  adIds: string[]
): Promise<{ archived_count: number }> {
  return api.post<{ archived_count: number }>(
    '/admin/ads/archive',
    { ad_ids: adIds }
  );
}

/**
 * Get a presigned upload URL for media files
 */
export async function getUploadUrl(
  filename: string,
  contentType: string
): Promise<UploadUrlResponse> {
  return api.post<UploadUrlResponse>('/admin/ads/upload-url', {
    filename,
    content_type: contentType,
  });
}

/**
 * Upload a file to the given upload key
 */
export async function uploadFile(
  uploadKey: string,
  file: File | Blob
): Promise<UploadResponse> {
  const arrayBuffer = await file.arrayBuffer();
  const contentType = file instanceof File ? file.type : 'application/octet-stream';
  return api.upload<UploadResponse>(`/admin/ads/upload/${uploadKey}`, arrayBuffer, contentType);
}

/**
 * Helper: Upload a file and get the final URL
 * Combines getUploadUrl and uploadFile into a single operation
 */
export async function uploadMedia(file: File): Promise<string> {
  // Get upload URL
  const { key } = await getUploadUrl(file.name, file.type);

  // Upload file
  const { url } = await uploadFile(key, file);

  return url;
}
